<script lang="ts">
	//Design system components
	import { Button, Radio, Select, Tooltip, toast } from '@Shelly/shelly-design-system';
	//Formatters
	import { format } from 'svelte-i18n';
	import { getContext, onDestroy } from 'svelte';
	import type { Writable } from 'svelte/store';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';
	import type { SidepanelStickyBottomProps } from 'src/components/Sidepanel/sidepanel-types';
	//Helpers
	import { get_local_ip, real_id } from '../../../../helpers/interface_helpers';
	import { tooltipText } from './tooltip_text';
	//Services
	import { localReq, postReq } from '../../../../services/http';
	//Stores
	import {
		getDeviceStatus,
		getDeviceSettings,
		readDeviceSettings,
	} from 'src/stores/device-runtime/status-selectors';
	import { devices_settings } from 'src/stores/device-runtime/status';
	//Types
	import type { SettingRequest } from '../../../../types/requests/settings/setting';

	export let device;

	const id = real_id(device.id);
	let settings = readDeviceSettings(id);
	$: settingsStore = getDeviceSettings(id);
	$: settings = $settingsStore ?? settings;
	let loading = false;

	const indicators = [
		{
			100: 'all',
			101: 'a',
			102: 'b',
			103: 'c',
		},
		{
			100: 'all',
			101: 'a',
			102: 'b',
			103: 'c',
		},
		{
			200: 'all',
			201: 'a',
			202: 'b',
			203: 'c',
		},
	]; //This is complicated setting, so I am leaving here first index as empty, until figure out better way to do it. Maybe will be refactored, but for proof of concept it works.
	/* new settings
		- Main settings
		http://192.168.205.79/settings?cf_output=%d(0,1,2,3) - Disabled
		URL: http://192.168.205.79/settings?cf_output=10%d - Active power
		URL: http://192.168.205.79/settings?cf_output=20$d - Apparent power
		- Subsettings
		- for active power
		URL: http://192.168.205.79/settings?cf_output=100 - All
		URL: http://192.168.205.79/settings?cf_output=101 - A
		URL: http://192.168.205.79/settings?cf_output=102 - B
		URL: http://192.168.205.79/settings?cf_output=103 - C
		- for apparent power
		URL: http://192.168.205.79/settings?cf_output=200 - All
		URL: http://192.168.205.79/settings?cf_output=201 - A
		URL: http://192.168.205.79/settings?cf_output=202 - B
		URL: http://192.168.205.79/settings?cf_output=203 - C
	*/

	let value = settings?.cf_output; //this is alway number, but because I am using it as object keys from indicators and to measure disabled (which can be 0,1,2,3) later it will became string
	let cache = value;
	$: disabled = value.toString().length === 1; //leaving this reactive, to hide checkholder's options

	let selectHolderOptions = [
		$format('option_disable'),
		$format('act_pow') || 'Active power(P, 1000 imp/kWh)',
		$format('app_pow') || 'Apparent power(S, 1000 imp/kWh)',
	];

	$: selectHolderValue = disabled ? 0 : /10\d/.test(value) ? 1 : /20\d/.test(value) ? 2 : ''; //this is used to know in which main option user is now

	const localIp = get_local_ip(id);

	const sendRequest = async () => {
		if (cache !== value) {
			let res: SettingRequest;
			loading = true;
			if (localIp) {
				res = await localReq(device, `${localIp}/settings`, 'post', {
					cf_output: value,
				});
			} else {
				res = await postReq(`device/generic_command`, {
					id: id,
					command: 'cf_output',
					payload: JSON.stringify({ cf_output: value }),
				});
			}
			if (res.isok) {
				settings.cf_output = value;
				devices_settings.update_devices_settings(id, settings);
				toast.open({
					type: 'success',
					title: $format('setting_scc_saved_toast'),
				});
				cache = value;
			} else {
				toast.open({ type: 'danger', title: $format('error_global') });
				value = cache;
			}
			loading = false;
		}
	};

	const handleChange = async (val) => {
		value = Number(`${value.toString().slice(0, 2)}${val}`);
	};

	const stickyBottomAction =
		getContext<Writable<SidepanelStickyBottomProps | null>>('stickyBottomAction');

	$: if (stickyBottomAction) {
		if ($showNewLayout) {
			stickyBottomAction.set({ onClick: sendRequest, loading, disabled: loading });
		} else {
			stickyBottomAction.set(null);
		}
	}

	onDestroy(() => {
		stickyBottomAction?.set(null);
	});

	const changeOptions = (e) => {
		//Changing the value if it was changed from select holder
		selectHolderValue = selectHolderOptions.indexOf(e.detail);
		switch (selectHolderValue) {
			case 0:
				//this will disable consumption indicators
				value = Number(value.toString().slice(-1));
				break;
			case 1:
				//this will enable active power consumption indicators
				value = Number(`10${value.toString().slice(-1)}`);
				break;
			case 2:
				//this will enable apparent power consumption indicators
				value = Number(`20${value.toString().slice(-1)}`);
				break;
			default:
				break;
		}
	};
</script>

<Select
	size="m"
	icon={'null'}
	options={selectHolderOptions}
	value={selectHolderOptions[selectHolderValue]}
	on:change={changeOptions}
	id="select-5a86dxq"
	aria-label={$format('sub_settings_title_consumption_indicator', {
		default: 'Consumption indicator',
	})}
/>

{#each Object.keys(indicators[selectHolderValue]) as indicator, i}
	<Tooltip placement="top" title={tooltipText({ message: $format('option_disabled') }, disabled)}>
		<Radio
			size="m"
			class="mr-l"
			{disabled}
			on:click={() => handleChange(i)}
			selected={value}
			value={+indicator}
			id="radio-11jl7mp"
			>{$format('consumption_indicator_source', {
				values: {
					value: indicators[selectHolderValue][indicator],
				},
			})}</Radio
		>
	</Tooltip>
{/each}

{#if !$showNewLayout}
	<Button class="mt-s" {loading} size="m" width="full" on:click={sendRequest}>
		{$format('button_save')}
	</Button>
{/if}
